import { IfAbility } from '@efdigital/ll-addon-auth';
import type { Cohort } from '@efdigital/ll-addon-data/types/xceed/cohort';
import AdminLegacyWarning from '@efdigital/ll-admin-v2/components/admin-legacy-warning.gts';
import { EditContext } from '@efdigital/ll-admin-v2/components/edit-context.gts';
import { CohortListing } from '@efdigital/ll-admin-v2/components/programs/cohort-listing.gts';
import { EditCohort } from '@efdigital/ll-admin-v2/components/programs/edit-cohort';
import config from '@efdigital/ll-admin-v2/config/environment';
import { findCohort } from '@efdigital/ll-admin-v2/data/builders/xceed/cohorts';
import { jsonApiOptions } from '@efdigital/ll-admin-v2/lib/json-api';
import type { ModelFrom } from '@efdigital/ll-admin-v2/lib/type-utils';
import type ProgramsProgramCohortRoute from '@efdigital/ll-admin-v2/routes/programs/program/cohort';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import type { Store } from '@warp-drive/core';
import { cacheKeyFor } from '@warp-drive/core';
import { Await, Request } from '@warp-drive/ember';
import { serializePatch } from '@warp-drive/utilities/json-api';
import { updateRecord } from '@warp-drive/utilities/json-api';
import { pageTitle } from 'ember-page-title';

interface IndexSignature {
  Args: {
    model: ModelFrom<ProgramsProgramCohortRoute>;
    controller: unknown;
  };
}

const XCEED_URL = config['ll-apps'].xceed;

export default class ProgramsProgramCohortIndexTemplate extends Component<IndexSignature> {
  @service declare store: Store;

  updateCohort = (currentCohort: Cohort, updates: Partial<Cohort>) => {
    // Filter out empty string values and convert dates to ISO format
    const cleanedUpdates: Partial<Cohort> = {};
    for (const [key, value] of Object.entries(updates)) {
      // Skip empty strings for date fields
      if ((key === 'starts' || key === 'ends') && value === '') {
        continue;
      }
      // Convert date strings to ISO format
      if (
        (key === 'starts' || key === 'ends') &&
        value &&
        typeof value === 'string'
      ) {
        // If it's in YYYY-MM-DD format, convert to ISO datetime
        // Add 'T00:00:00Z' to prevent timezone shift when converting to ISO
        if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
          cleanedUpdates[key as keyof Cohort] = new Date(
            value + 'T00:00:00Z'
          ).toISOString() as never;
          continue;
        }
      }
      cleanedUpdates[key as keyof Cohort] = value as never;
    }

    Object.assign(currentCohort, cleanedUpdates);

    const init = updateRecord(currentCohort, {
      ...jsonApiOptions,
      patch: true,
    });
    init.body = JSON.stringify(
      serializePatch(this.store.cache, cacheKeyFor(currentCohort))
    );
    init.headers.set('Content-Type', 'application/vnd.api+json');

    return this.store.request(init);
  };

  <template>
    <div class="container py-5">
      {{pageTitle "Index"}}
      <IfAbility @ability="cohorts" @atLeast="viewer">
        <:if>
          <Request @query={{findCohort @model.cohort_id}}>
            <:content as |content|>
              <AdminLegacyWarning
                @item={{content.data}}
                @itemType="cohort"
                @programId={{@model.program_id}}
              />

              <div class="row g-5">
                <IfAbility @ability="cohorts" @atLeast="editor">
                  <:if>
                    <EditContext as |context|>
                      <div class="col-md-8">
                        <EditCohort
                          @item={{content.data}}
                          @context={{context}}
                        />
                      </div>
                    </EditContext>
                  </:if>
                </IfAbility>
                <div class="col-md-4">
                  <h3 class="gud-eyebrow">Preview</h3>
                  <CohortListing @cohort={{content.data}} />

                  <Await @promise={{content.data.program}}>
                    <:success as |program|>
                      <div class="my-4">
                        <a
                          href="{{XCEED_URL}}/program/{{program.slug}}/{{content.data.slug}}"
                          class="btn btn-primary"
                          target="_blank"
                          rel="noreferrer noopener"
                        >Open in Xceed
                          <iconify-icon icon="ph:arrow-square-in-bold" /></a>
                      </div>
                    </:success>
                  </Await>
                </div>
              </div>
            </:content>
          </Request>
        </:if>
        <:else>
          <div class="alert alert-warning" role="alert">
            <h4 class="alert-heading">Access Denied</h4>
            <p>You do not have permission to view cohorts.</p>
            <p class="mb-0">Please contact your administrator if you believe you
              should have access.</p>
          </div>
        </:else>
      </IfAbility>
    </div>
  </template>
}
